/**
 * Pop-up windows for the storefront and the dashboard.
 *
 * A window is a `<dialog>` carrying `x-modal="itsName"`. Opening one calls `showModal()`, so the
 * browser supplies the dimmed page behind it, Escape, focus containment, focus returning to whatever
 * opened it, click-outside-to-close, and making the rest of the page inert. This module adds the
 * parts it does not: a name to address a window by from anywhere, one-at-a-time behaviour, a scroll
 * lock, and lifecycle events.
 *
 * ## The markup
 *
 * ```erb
 * <%# The window. `.modal-header` and `.modal-body` are optional; `overlays.css` styles both. %>
 * <dialog x-modal="contactModal" aria-labelledby="contact_title">
 *   <div class="modal-header">
 *     <h2 id="contact_title">Contact Us</h2>
 *   </div>
 *   <div class="modal-body">
 *     <%= render "message_contacts/message_contact_form" %>
 *   </div>
 *   <button type="button" x-modal-close aria-label="Close">&times;</button>
 * </dialog>
 *
 * <%# Anything that opens it. A button, because it acts rather than navigates. %>
 * <button type="button" x-modal-open="contactModal">Contact us</button>
 * ```
 *
 * ## The attributes
 *
 * | Attribute | Put it on | Does |
 * | --- | --- | --- |
 * | `x-modal="name"` | the `<dialog>` | names the window so anything can address it |
 * | `x-modal-open="name"` | any element | opens that window when clicked |
 * | `x-modal-close` | any element inside a window | closes the window around it |
 * | `x-modal-close="name"` | any element | closes that window by name |
 * | `x-modal-toggle="name"` | any element | opens it if closed, closes it if open |
 * | `x-modal.open-on-load` | the `<dialog>` | opens as soon as it renders, for a page that should arrive with the window already up |
 * | `data-reveal-id="name"` | any element | Foundation's old opener, still honoured because store footers in the database carry it. Prefer `x-modal-open` in anything we author |
 *
 * ## From JavaScript
 *
 * ```js
 * Alpine.store("modal").open("contactModal")
 * Alpine.store("modal").close()                       // whatever is open
 * Alpine.store("modal").isOpen("contactModal")        // => true
 * Alpine.store("modal").toggle("contactModal")        // open if closed, closed if open
 * Alpine.store("modal").current                        // => "contactModal"
 * ```
 *
 * ## From an Alpine expression
 *
 * ```html
 * <button x-on:click="$store.modal.open('contactModal')">Contact us</button>
 * <span x-show="$store.modal.isOpen('contactModal')">The window is up</span>
 * ```
 *
 * ## A window built by a script
 *
 * Panels appended after the page loads work too, and may be opened on the same tick as the append.
 * Give one an `id` matching its name: a panel is normally found by the name it registered when
 * Alpine walked it, and until that has happened it is looked up by id instead.
 *
 * ```js
 * $("#note_modal").remove()
 * $("body").append(`
 *   <dialog id="note_modal" x-modal="note_modal" aria-labelledby="note_title">
 *     <div class="modal-header"><h2 id="note_title">Notes</h2></div>
 *     <div class="modal-body">...</div>
 *     <button type="button" x-modal-close aria-label="Close">&times;</button>
 *   </dialog>
 * `)
 * Alpine.store("modal").open("note_modal")   // opens straight away, before Alpine sees it
 * ```
 *
 * ## Events
 *
 * Each fires on the panel and bubbles, so listen anywhere, including delegated:
 *
 * ```js
 * $(document).on("modal:opened", '[x-modal="contactModal"]', () => trackOpen())
 * $(document).on("modal:closed", '[x-modal="contactModal"]', () => resetForm())
 * ```
 *
 * `modal:open` and `modal:close` fire before the window moves, `modal:opened` and `modal:closed`
 * after. `modal:closed` also covers the closes this module does not drive -- Escape, and a click
 * outside the panel -- because it is emitted from the dialog's own `close` event.
 *
 * ## Sizing
 *
 * One width for now, `min(90vw, 36rem)`, in `entrypoints/overlays.css`. Windows that need another
 * bring a class with them as they convert.
 *
 * ## Worth knowing
 *
 * - A panel **must** be a `<dialog>`. Anything else is refused with a console warning.
 * - Windows are addressed by name, not by id, because several partials render the same id. A
 *   duplicate name on one page warns, and the first one wins. A server-rendered panel needs no id;
 *   only one injected by a script and opened immediately does, as above.
 * - Opening a window while another is open closes the first: only one is ever up.
 * - Opening a name nothing answers to warns rather than failing silently.
 */
import type Alpine from "alpinejs"

// Kept from Foundation because store-authored footer HTML in the database carries it.
const LEGACY_OPENERS = "[data-reveal-id]"

/**
 * A window, addressed either by its `x-modal` name or by any element inside it -- which is what lets
 * a close button say `$store.modal.close($el)` without repeating the name.
 */
type ModalTarget = string | Element | null | undefined

export type ModalStore = {
  /** Name of the open modal, or null. Only one is ever open. */
  current: string | null

  /**
   * Opens a window, closing whichever is already open.
   *
   * @example Alpine.store("modal").open("contactModal")
   * @example Alpine.store("modal").open(document.querySelector("dialog[x-modal]"))
   */
  open(target: ModalTarget): void

  /**
   * Closes a window. With no argument, closes whatever is open, which is what a global Escape or a
   * "done" callback wants.
   *
   * @example Alpine.store("modal").close()
   * @example Alpine.store("modal").close("contactModal")
   * @example <button x-on:click="$store.modal.close($el)">Not now</button>
   */
  close(target?: ModalTarget): void

  /**
   * @example x-show="$store.modal.isOpen('contactModal')"
   * @returns true when that window is the open one
   */
  isOpen(target: ModalTarget): boolean

  /**
   * Opens the window if it is closed, closes it if it is open.
   *
   * @example Alpine.store("modal").toggle("contactModal")
   */
  toggle(target: ModalTarget): void
}

/**
 * Registers the `modal` store and the `x-modal` directive. Called once from
 * `entrypoints/alpine.ts`, before `Alpine.start()`.
 *
 * @example
 * import Alpine from "alpinejs"
 * import { initModalStore } from "~/src/alpine/modalStore"
 *
 * initModalStore(Alpine)
 * Alpine.start()
 */
export function initModalStore(alpine: typeof Alpine) {
  // Names, not ids: several partials render the same id and `getElementById` picks one silently.
  const panels = new Map<string, HTMLDialogElement>()

  function register(name: string, el: HTMLDialogElement) {
    const existing = panels.get(name)
    if (existing && existing !== el && existing.isConnected) {
      console.warn(`[x-modal] two modals are named "${name}" on this page; the first one wins`)
      return
    }

    panels.set(name, el)
  }

  function nameOf(el: Element) {
    const panel = el.closest<HTMLElement>("[x-modal]")

    return panel?.getAttribute("x-modal") || null
  }

  function resolve(target: ModalTarget): string | null {
    if (typeof target === "string") return target
    if (target instanceof Element) return nameOf(target)

    return null
  }

  /** Without the warning, for callers that are only asking whether a window is ours. */
  function findPanel(name: string | null) {
    if (!name) return null

    const panel = panels.get(name)
    if (panel?.isConnected) return panel

    // Modals appended as HTML strings only register once Alpine has walked them.
    const found = document.getElementById(name)

    return found instanceof HTMLDialogElement ? found : null
  }

  function panelFor(name: string | null) {
    const panel = findPanel(name)

    if (!panel) console.warn(`[x-modal] no <dialog> named "${name}" on this page`)

    return panel
  }

  /**
   * A modal dialog leaves the page inert but still scrollable. The padding stands in for the
   * scrollbar the lock takes away, so the page behind does not shift.
   */
  function lockScroll() {
    const root = document.documentElement
    const gap = window.innerWidth - root.clientWidth

    root.style.overflow = "hidden"
    if (gap > 0) root.style.paddingRight = `${gap}px`
  }

  function unlockScroll() {
    document.documentElement.style.overflow = ""
    document.documentElement.style.paddingRight = ""
  }

  /**
   * The checkout panels keep every control inside a shadow root, where the browser's own autofocus
   * does not look, so it leaves focus on the page behind. The tabindex is what lets the panel itself
   * accept focus; Tab carries on into the shadow root from there. Deferred a frame because those
   * panels are also filled in after they are opened.
   */
  function focusPanel(panel: HTMLDialogElement) {
    requestAnimationFrame(() => {
      if (!panel.open || panel.contains(document.activeElement)) return

      panel.tabIndex = -1
      panel.focus()
    })
  }

  function emit(panel: HTMLElement, name: string) {
    panel.dispatchEvent(new CustomEvent(`modal:${name}`, { bubbles: true }))
  }

  // Set here rather than on each panel so the ones built as HTML strings are covered too.
  const nativeLightDismiss = "closedBy" in HTMLDialogElement.prototype

  const definition: ModalStore = {
    current: null,

    open(target) {
      const name = resolve(target)
      const panel = panelFor(name)
      if (!name || !panel) return
      // `dashboard/request_orders` removes and re-injects its panel on every click, so the same
      // name routinely means a fresh element that still needs opening.
      if (this.current === name && panel.open) return

      if (this.current && this.current !== name) this.close(this.current)

      emit(panel, "open")

      if (nativeLightDismiss) panel.setAttribute("closedby", "any")
      panel.showModal()
      focusPanel(panel)
      lockScroll()

      this.current = name

      emit(panel, "opened")
    },

    close(target) {
      const name = target === undefined ? this.current : resolve(target)
      const panel = panelFor(name)
      if (!name || !panel || this.current !== name) return

      emit(panel, "close")

      // The panel's own `close` event clears `current`, unlocks scrolling and emits `modal:closed`,
      // so the ways a dialog closes on its own -- Escape, a click outside it -- go down this path
      // too.
      panel.close()
    },

    isOpen(target) {
      const name = resolve(target)

      return name !== null && this.current === name
    },

    toggle(target) {
      if (this.isOpen(target)) {
        this.close(target)
      } else {
        this.open(target)
      }
    },
  }

  alpine.store("modal", definition)

  // Read back rather than closing over `definition`: mutations through the raw object, outside the
  // reactive proxy `Alpine.store` wraps it in, notify no effects.
  const store = alpine.store("modal") as ModalStore

  // A root selector is what lets these work on markup carrying no `x-data`.
  alpine.addRootSelector(() => "[x-modal], [x-modal-open], [x-modal-close], [x-modal-toggle]")

  /**
   * The three triggers, one directive each.
   *
   * ```html
   * <button x-modal-open="contactModal">Contact us</button>
   * <button x-modal-close>Close</button>                     <!-- the window it sits in -->
   * <button x-modal-close="contactModal">Not now</button>     <!-- that window, from anywhere -->
   * <button x-modal-toggle="contactModal">Contact us</button>
   * ```
   *
   * `open` needs a name to open. `close` and `toggle` fall back to the window around them, so a
   * button inside a panel needs no name.
   */
  function trigger(action: "open" | "close" | "toggle") {
    return (
      el: Element,
      { expression }: { expression: string },
      { cleanup }: { cleanup: (fn: () => void) => void }
    ) => {
      if (!(el instanceof HTMLElement)) return

      const run = (event: Event) => {
        event.preventDefault()

        if (action === "open") store.open(expression)
        if (action === "close") store.close(expression || el)
        if (action === "toggle") store.toggle(expression || el)
      }

      el.addEventListener("click", run)
      cleanup(() => el.removeEventListener("click", run))
    }
  }

  alpine.directive("modal-open", trigger("open"))
  alpine.directive("modal-close", trigger("close"))
  alpine.directive("modal-toggle", trigger("toggle"))

  /**
   * The window itself.
   *
   * ```html
   * <dialog x-modal="contactModal">…</dialog>
   * <dialog x-modal="contactModal" x-modal.open-on-load>   <!-- opens as it renders -->
   * ```
   */
  alpine.directive("modal", (el, { expression, modifiers }, { cleanup }) => {
    if (!(el instanceof HTMLElement)) return

    // Opens the panel it sits on, so it takes no expression, the way `x-modal-close` addresses the
    // panel around it. The template decides when to render it; the store decides nothing.
    if (modifiers.includes("open-on-load")) {
      store.open(el)

      return
    }

    if (!(el instanceof HTMLDialogElement)) {
      console.warn(`[x-modal] "${expression}" must be a <dialog>, not a <${el.tagName.toLowerCase()}>`)
      return
    }

    register(expression, el)

    cleanup(() => {
      if (panels.get(expression) === el) panels.delete(expression)
    })
  })

  // One listener for every way a panel closes, including the browser's own: Escape, and a click
  // outside the panel.
  document.addEventListener(
    "close",
    (event) => {
      const panel = event.target
      if (!(panel instanceof HTMLDialogElement) || !panel.hasAttribute("x-modal")) return

      if (store.current === panel.getAttribute("x-modal")) store.current = null
      if (!store.current) unlockScroll()

      emit(panel, "closed")
    },
    true
  )

  // `closedby="any"`, set in `open`, is the browser's own click-outside-to-close. This stands in
  // where that attribute is not understood.
  if (!nativeLightDismiss) {
    document.addEventListener("pointerdown", (event) => {
      const panel = store.current ? panelFor(store.current) : null
      if (!panel?.open) return

      const box = panel.getBoundingClientRect()
      const outside =
        event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom

      if (outside) store.close()
    })
  }

  // Capture phase: Foundation's own `[data-reveal-id]` handler is still bound, and it is a classic
  // script, so it always binds before this module and only capturing beats it.
  document.addEventListener(
    "click",
    (event) => {
      const opener = (event.target as Element | null)?.closest(LEGACY_OPENERS)
      if (!opener) return

      // Windows still on Foundation's reveal keep this attribute too, and so does a footer in the
      // database naming a window that no longer exists. Only take the click when the window is one
      // of ours; otherwise leave the event alone and let Foundation have it.
      const name = opener.getAttribute("data-reveal-id")
      if (!findPanel(name)) return

      event.preventDefault()
      event.stopPropagation()
      store.open(name)
    },
    true
  )
}
